import { extractFileNameFromFullPath } from '@/util/filepath-utils'
import { kUnnamedFileFallback, kUnnamedReferenceFallback, kUntitledCaseFallback, kUntitledLawFallback, kUnnamedWritingSampleFallback } from '../constants'
import { AgentReferenceType, ClientAgentReference } from '../store/schemas'

/**
 * Determines the title for a given agent reference based on its metadata.
 *
 * @param reference - The agent reference object.
 * @returns The title string to display.
 */
export function agentReferenceMetadataToTitle(reference: ClientAgentReference): string {
  const fileReference = canonicalFileReference(reference)
  if (fileReference?.filename) return fileReference.filename

  switch (reference.type) {
    case AgentReferenceType.USER_FILE_QDRANT_CHUNK:
    case AgentReferenceType.ASSET_EVENT:
      // Backend sends `filename` directly
      return reference.metadata.filename || kUnnamedFileFallback
    case AgentReferenceType.FILE_UPLOAD_CHUNK:
    case AgentReferenceType.FILE_UPLOAD_FULL: {
      // Legacy pipeline: `file_path` ends with the original filename
      const filename = extractFileNameFromFullPath(reference.metadata.file_path, kUnnamedFileFallback)
      return filename
    }
    case AgentReferenceType.CASELAW: {
      // Prefer bluebook citation, fall back to title
      return reference.metadata.bb_citation || reference.metadata.title || kUntitledCaseFallback
    }
    case AgentReferenceType.LRR: {
      // The name says what the law is; the citation is what a lawyer would cite.
      const { name, citation } = reference.metadata
      if (name && citation) return `${name} — ${citation}`
      return name || citation || reference.metadata.slug || kUntitledLawFallback
    }
    case AgentReferenceType.TRELLIS_WRITING_SAMPLE:
      return reference.metadata.title || kUnnamedWritingSampleFallback
    default: {
      return kUnnamedReferenceFallback
    }
  }
}

/**
 * Citator-derived case status for caselaw references; `undefined` for all other types.
 * Backend populates this only on caselaw metadata objects.
 */
export function getCaseStatusFromReference(reference: ClientAgentReference): string | null | undefined {
  if (reference.type !== AgentReferenceType.CASELAW) return undefined
  return reference.metadata.case_status
}

function canonicalFileReference(reference: ClientAgentReference) {
  if (reference.type !== AgentReferenceType.USER_FILE_QDRANT_CHUNK && reference.type !== AgentReferenceType.ASSET_EVENT) return null
  const fileReference = reference.file_reference
  if (!fileReference) return null
  return fileReference
}
